import React, { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { QueryClient, QueryClientProvider } from "react-query";
// HUB-440 (EDDM review-for-discount): reuse the ADM review-offer components
// in place — they are NOT ADM-coupled (they hit the eligibility endpoint by
// client/session and render a self-contained row). A later PR can lift this
// folder to a shared path (e.g. campaign-builder/checkout/reviewOffer); until
// then EDDM imports it from its current ADM location.
// TODO(HUB-440): move src/campaign-builder/channels/directmail/adm/order-details/reviewOffer
// to a shared location and update both ADM and EDDM imports.
import {
  ReviewOfferRow,
  useDiscountExclusivity,
  DiscountSwapDialog,
  ReviewOfferEntitlement,
  DiscountChoice,
} from "campaign-builder/channels/directmail/adm/order-details/reviewOffer";

// ReviewOfferRow's useReviewOfferEligibility calls react-query's useQuery, which
// reads the client from context on every render even when disabled. The EDDM
// route is not wrapped in a QueryClientProvider (only CAMPAIGN_BUILDER is), so
// provide one here to keep the submit page from blanking. (PAD-32207)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

export type EddmReviewOfferProps = {
  campaignId?: string | number;
  countryCode?: string;
  /** campaign.stripeCouponCode is set — a manual coupon is persisted on the order */
  hasStripeCoupon: boolean;
  /**
   * Reuse EDDM's EXISTING coupon-clear path (updateCouponCode with a null code,
   * same call the coupon "remove"/empty flow already makes) — no new
   * coupon-clearing logic is introduced here.
   */
  onClearCoupon: () => Promise<void> | void;
  /**
   * Lifts useDiscountExclusivity's discountChoice up to the EDDM submit
   * container, which needs it to derive `discount_choice` on the legacy
   * embeddedCheckoutCampaign request. Mirrors ADM's OrderDetailsDrawer
   * onDiscountChoiceChange lift (docs/review-discount-payments-integration.md
   * §2.0).
   */
  onDiscountChoiceChange?: (choice: DiscountChoice) => void;
  /**
   * Lifts the resolved entitlement up so the EDDM quote can render the
   * "Review discount (x%)" line and subtract it from the displayed total —
   * ADM does the same inside its OrderDetailsDrawer.
   */
  onEntitlementChange?: (entitlement: ReviewOfferEntitlement | null) => void;
};

/**
 * EDDM's review-for-discount surface. Self-guards on is_review_discount_enabled
 * (ReviewOfferRow returns null when the flag is off, so this renders zero DOM
 * footprint), and owns the "one discount per order" exclusivity + swap dialog
 * exactly like ADM's OrderDetailsDrawer. Kept as a typed wrapper so all of the
 * review-offer logic stays in TS while the legacy EDDM submit page remains .js.
 */
export const EddmReviewOffer = ({
  campaignId,
  countryCode,
  hasStripeCoupon,
  onClearCoupon,
  onDiscountChoiceChange,
  onEntitlementChange,
}: EddmReviewOfferProps) => {
  const posthog = usePostHog();
  const [reviewEntitlement, setReviewEntitlement] = useState<ReviewOfferEntitlement | null>(null);
  const hasReviewEntitlement = !!reviewEntitlement && reviewEntitlement.status === "active";

  const {
    discountChoice,
    swapDirection,
    isSwapping,
    swapError,
    isSwapBusy,
    openSwapDialog,
    startPendingReviewSwap,
    confirmSwap,
    cancelSwap,
  } = useDiscountExclusivity({
    hasStripeCoupon,
    hasReviewEntitlement,
    onClearCoupon,
    onSwapped: () => posthog?.capture("review_discount_swapped", { event_category: "Quote", campaign_id: campaignId }),
  });

  // Lift discountChoice up so the EDDM submit container can send it as
  // `discount_choice` on the checkout request.
  useEffect(() => {
    onDiscountChoiceChange?.(discountChoice);
  }, [discountChoice, onDiscountChoiceChange]);

  // Render the row BARE (no wrapper box), exactly like ADM's OrderDetailsDrawer:
  // ReviewOfferRow returns null when the flag is off (prod default) or the
  // client isn't eligible, so a wrapper div would leave an empty bordered box
  // for essentially every EDDM user. The row owns its own DOM + spacing.
  return (
    <QueryClientProvider client={queryClient}>
      <ReviewOfferRow
        campaignId={campaignId}
        countryCode={countryCode}
        discountChoice={discountChoice}
        onRequestSwapToReview={() => openSwapDialog("couponToReview")}
        onStartPendingReviewSwap={startPendingReviewSwap}
        isSwapBusy={isSwapBusy}
        onEntitlementChange={(entitlement) => {
          setReviewEntitlement(entitlement);
          onEntitlementChange?.(entitlement);
        }}
      />

      {/* HUB-454: "one discount per order" swap confirmation (frontend-spec.md S6). */}
      <DiscountSwapDialog
        direction={swapDirection}
        program={reviewEntitlement?.program}
        percent={reviewEntitlement?.discount_percent}
        isSubmitting={isSwapping}
        hasError={swapError}
        onCancel={cancelSwap}
        onConfirm={confirmSwap}
      />
    </QueryClientProvider>
  );
};

export default EddmReviewOffer;
